/**
 * Светлая и тёмная схема.
 *
 * Атрибут на `<html>` выставляет скрипт в index.html до первой отрисовки,
 * чтобы не мигала светлая тема. antd читает те же `--kit-*`: алгоритм
 * только достраивает палитру, явные токены совпадают с оболочкой.
 */

import { theme, type ThemeConfig } from "antd";
import { create } from "zustand";

const THEME_KEY = "sl1ce.theme";

function isDark(): boolean {
  return (
    document.documentElement.getAttribute("data-prefers-color-scheme") ===
    "dark"
  );
}

function apply(dark: boolean): void {
  const next = dark ? "dark" : "light";
  document.documentElement.setAttribute("data-prefers-color-scheme", next);
  localStorage.setItem(THEME_KEY, next);
}

interface ThemeState {
  dark: boolean;
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set) => ({
  dark: isDark(),
  toggle: () => {
    const dark = !isDark();
    apply(dark);
    set({ dark });
  },
}));

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .replace(/\s+/g, " ")
    .trim();
}

export function antdThemeOf(dark: boolean): ThemeConfig {
  return {
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: cssVar("--kit-primary"),
      colorSuccess: cssVar("--kit-success"),
      colorWarning: cssVar("--kit-warning"),
      colorError: cssVar("--kit-error"),
      colorInfo: cssVar("--kit-primary"),
      colorText: cssVar("--kit-text"),
      colorTextSecondary: cssVar("--kit-text-secondary"),
      colorTextTertiary: cssVar("--kit-text-tertiary"),
      colorTextQuaternary: cssVar("--kit-text-quaternary"),
      colorBgLayout: cssVar("--kit-bg"),
      colorBgContainer: cssVar("--kit-bg-secondary"),
      colorBgElevated: cssVar("--kit-bg-secondary"),
      colorBorder: cssVar("--kit-border"),
      colorBorderSecondary: cssVar("--kit-border-secondary"),
      colorFillSecondary: cssVar("--kit-fill-secondary"),
      colorLink: cssVar("--kit-primary"),
      borderRadius: 6,
      fontFamily: cssVar("--kit-font"),
      fontSize: 14,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
    },
  };
}
